import './polyfills/intersectionObserver'
import './polyfills/resizeObserver'
import './polyfills/preventDefault'
import './polyfills/urlSearchParams'
import './polyfills/url'
import './polyfills/objectFit'
import './polyfills/smoothScroll'
import React from 'react'
import ReactDOM from 'react-dom'
import App from '#src/containers/App'
import { ApolloProvider } from '@apollo/react-common'
import { Router } from 'react-router-dom'
import { createBrowserHistory } from 'history'
import { HelmetProvider } from 'react-helmet-async'
import {
  Analytics,
  ConsoleTracker,
  SegmentTracker,
  FacebookTracker,
  FacebookTrackerOptions,
  FullStoryTracker,
  FullStoryTrackerOptions,
  LiveSessionTracker,
  LiveSessionTrackerOptions,
  SmartLookTracker,
  SmartLookTrackerOptions
} from '@deal/web-tracking'
import { AnalyticsProvider } from '#src/containers/Analytics'
import { IdentityProvider } from '#src/containers/Identity'
import { ConfigProvider } from '#src/containers/Config'
import { HostnameProvider, Hostname } from '#src/containers/Hostname'
import { UserAgentProvider } from '#src/containers/UserAgent'
import { CookieProvider } from '#src/context/cookies'
import { PageKeyProvider } from '@deal/page-key'
import config from '#src/config'
import Loadable from 'react-loadable'
import fragmentTypes from '#src/generated/schema.fragments'
import { browserDealApiClient, DealViewerContext } from '@deal/api-client-js'
import UserExperimentClientProvider from '#src/containers/UserExperimentClientProvider'
import {
  createBrowserExperimentClient,
  BrowserExperimentClientConfig,
  ExperimentViewedInput
} from '@deal/experiment-js'
import { ErrorResponse } from 'apollo-link-error'
import Heartbeat from '#src/components/Heartbeat'
import SendTokensToNative from '#src/services/sendTokensToNative'
import { getForceTraceId } from '#src/services/tracing'
import { loginLocation } from '#src/services/routing'
import { init as SentryInit } from '@sentry/browser'
import Logger from '#src/services/logger'
import {
  WebMessageEventEmitter,
  WebMessageEventEmitterProvider
} from '#src/containers/ReceiveMessageFromNative'
import { AnalyticsOptions } from '@deal/web-tracking/client'
import trimStart from 'lodash/trimStart'
import cookies from 'js-cookie'
import uuidv4 from 'uuid/v4'
import { MyselfDocument, MyselfQuery } from './generated/graphql'

// Exception handling
if (config.get('sentry.enabled')) {
  SentryInit({
    dsn: config.get('sentry.webDsn'),
    environment: config.get('environment'),
    release: process.env.SENTRY_RELEASE
  })
}

const fullHostname = window.location.hostname.toLowerCase()
let hostname = Hostname.Deal
let isGcp = false
if (fullHostname.endsWith(config.get('gcpHostSuffix'))) {
  hostname = Hostname.Curated
  isGcp = true
} else if (fullHostname.endsWith(config.get('curatedHostSuffix'))) {
  hostname = Hostname.Curated
}

// GraphQL API connection details
let api = config.get('api')

if (isGcp) {
  api = config.get('apiGcp')
} else if (hostname === Hostname.Curated) {
  api = config.get('apiCurated')
}

// Allow override of optimizely variations via the url
const urlQueryString = window.location.search
const urlParams = new URLSearchParams(urlQueryString)
const experimentOverrides = urlParams.get('experimentOverrides') || undefined
const forceDefaultTreatments =
  urlParams.get('forceDefaultTreatments') === 'true' || window.__IS_BOT__
const experimentBucketingKey = urlParams.get('experimentBucketingKey') || undefined

// Callback for Apollo GraphQL errors
const handleError = (error: ErrorResponse) => {
  const { graphQLErrors, networkError } = error

  if (graphQLErrors) {
    graphQLErrors.forEach(error => {
      const { message, locations, path } = error

      Logger.captureMessage(message, {
        path,
        locations
      })
    })
  }
  if (networkError) {
    Logger.captureException(networkError)
  }
}

// History to be used by react router
const history = createBrowserHistory()

const handleNetworkError = (networkError: Error) => {
  // @ts-ignore `networkError` is incompletely typed
  //   See: https://github.com/apollographql/apollo-link/issues/300
  if (networkError && networkError.statusCode == 401) {
    // Redirect to login on 401
    history.push(loginLocation(history.location))
  }
}

const sendTokensToNative = new SendTokensToNative()
// Bootstrap the native client with the initial state of the tokens
window.addEventListener('load', sendTokensToNative.sendIfChanged)

// Intercept DealNativeBridge.sendToWeb calls, dispatching them through this event listener
const webMessageEventEmitter = new WebMessageEventEmitter()
webMessageEventEmitter.interceptBridgeSendToWeb()

// GraphQL client
const { apolloClient } = browserDealApiClient({
  protocol: api.protocol,
  webSocketProtocol: api.webSocketProtocol,
  host: api.host,
  port: api.port,
  introspectionQueryResultData: fragmentTypes,
  initialCacheState: window.__APOLLO_STATE__,
  experimentOverrides,
  forceTrace: () => getForceTraceId(window.location.href),
  isBot: () => window.__IS_BOT__,
  viewerContext: () => DealViewerContext.CONSUMER,
  onError: handleError,
  onNetworkError: handleNetworkError,
  links: [sendTokensToNative.sendIfChangedOnResponseLink()]
})
delete window.__APOLLO_STATE__

// Tracking
const trackers: AnalyticsOptions['trackers'] = []

if (config.get('tracking.console.enabled')) {
  trackers.push(client => new ConsoleTracker(client))
}

if (config.get('tracking.segment.enabled')) {
  const segmentAnalytics = window.analytics
  if (segmentAnalytics) {
    trackers.push(client => new SegmentTracker(client))
  }
}

if (config.get('tracking.facebook.enabled')) {
  const facebookPixel = window.fbq
  if (!!facebookPixel) {
    const facebookOptions: FacebookTrackerOptions = {
      pixelIds: config.get('tracking.facebook.pixels')
    }

    trackers.push(client => new FacebookTracker(client, facebookOptions))
  }
}

if (config.get('tracking.fullstory.enabled')) {
  const fullStoryClient = window.FS
  if (fullStoryClient) {
    const fullStoryOptions: FullStoryTrackerOptions = {
      sampleRate: config.get('tracking.fullstory.sample_rate')
    }

    trackers.push(client => new FullStoryTracker(client, fullStoryOptions))
  }
}

if (config.get('tracking.livesession.enabled')) {
  const liveSessionClient = window.__ls
  if (liveSessionClient) {
    const liveSessionOptions: LiveSessionTrackerOptions = {
      sampleRate: config.get('tracking.livesession.sample_rate')
    }

    trackers.push(client => new LiveSessionTracker(client, liveSessionOptions))
  }
}

if (config.get('tracking.smartlook.enabled')) {
  const smartLookClient = window.smartlook
  if (smartLookClient) {
    const smartLookOptions: SmartLookTrackerOptions = {
      sampleRate: config.get('tracking.smartlook.sample_rate')
    }

    trackers.push(client => new SmartLookTracker(client, smartLookOptions))
  }
}

if (window.__CYPRESS_TRACKER_FACTORY__) {
  trackers.push(window.__CYPRESS_TRACKER_FACTORY__)
}

// Rendering
apolloClient
  .query<MyselfQuery>({
    query: MyselfDocument
  })
  .then(({ data }) => {
    const userId = data && data.me && data.me.id
    const realUserId = data && data.me && data.me.realUser.id
    const sessionId = data.session.id

    const analytics = new Analytics({
      trackers,
      sessionId
    })

    if (userId !== realUserId) {
      analytics.disable()
    }

    if (userId) {
      analytics.identify(userId)
    }

    // Update RUM page label and @deal/web-tracking if page-key changes
    const onPageKeyChange = (pageKey: string | null) => {
      analytics.setPageKey(pageKey || undefined)

      if (pageKey) {
        if (window.LUX) {
          window.LUX.label = pageKey
        }
      } else {
        if (window.LUX) {
          delete window.LUX.label
        }
      }
    }

    const queryParams = new URLSearchParams(trimStart(window.location.search, '?'))
    const previousClientSessionId = cookies.get('curated_client_session_id')
    const shouldResetClientSession =
      queryParams.has('utm_source') ||
      queryParams.has('gclid') ||
      queryParams.has('fbclid') ||
      !previousClientSessionId

    if (shouldResetClientSession) {
      const newClientSessionId = uuidv4()

      analytics.events.clientSessionReset({
        current_id: newClientSessionId,
        previous_id: previousClientSessionId
      })

      cookies.set('curated_client_session_id', newClientSessionId)
    }

    // Experiment client
    const fallbackTreatments = window.__EXPERIMENT_TREATMENTS__

    const handleExperimentViewed = ({
      experiment,
      variation,
      changeNumber,
      nonInteraction
    }: ExperimentViewedInput) => {
      analytics.events.experimentViewed({
        experiment,
        variation,
        change_number: changeNumber,
        non_interaction: nonInteraction
      })

      /**
       * Add experiment & variation data to SpeedCurve beacon so we can segment LUX user's by treatment
       */
      if (window.LUX) {
        window.LUX.addData(experiment, variation)
      }
    }

    const experimentClientConfig: BrowserExperimentClientConfig = {
      graphqlConfig: {
        apolloClient,
        fetchUpdatesIntervalMillis: 60000
      },
      onExperimentViewed: handleExperimentViewed,
      experimentOverrides,
      forceDefaultTreatments,
      fallback: fallbackTreatments
    }
    const experimentClient = createBrowserExperimentClient(experimentClientConfig)
    delete window.__OPTIMIZELY_DATAFILE__
    delete window.__EXPERIMENT_TREATMENTS__

    // Rendering method
    const renderer = window.__APP_RENDERER__ || 'client'
    delete window.__APP_RENDERER__

    // Rendering function
    const render = (AppComponent: React.ComponentType) => {
      const Root = (
        <ConfigProvider config={config}>
          <ApolloProvider client={apolloClient}>
            <CookieProvider environment={window}>
              <UserAgentProvider>
                <AnalyticsProvider client={analytics}>
                  <IdentityProvider>
                    <Heartbeat />
                    <UserExperimentClientProvider
                      experimentClient={experimentClient}
                      overrideId={experimentBucketingKey}
                    >
                      <HostnameProvider hostname={hostname}>
                        <WebMessageEventEmitterProvider value={webMessageEventEmitter}>
                          <Router history={history}>
                            <PageKeyProvider onNewPageKey={onPageKeyChange}>
                              <HelmetProvider>
                                <AppComponent />
                              </HelmetProvider>
                            </PageKeyProvider>
                          </Router>
                        </WebMessageEventEmitterProvider>
                      </HostnameProvider>
                    </UserExperimentClientProvider>
                  </IdentityProvider>
                </AnalyticsProvider>
              </UserAgentProvider>
            </CookieProvider>
          </ApolloProvider>
        </ConfigProvider>
      )

      if (renderer === 'server') {
        Loadable.preloadReady().then(() => {
          ReactDOM.hydrate(Root, document.getElementById('deal'))
        })
      } else {
        ReactDOM.render(Root, document.getElementById('deal'))
      }
    }

    render(App)
  })
